import React from 'react';

import { useIntl } from 'react-intl-next';

import ErrorIcon from '@atlaskit/icon/core/migration/error';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { Flex } from '@atlassian/eoc-flex';
import { RuleItemGroup, RuleItemRow } from '@atlassian/eoc-og-rule';
import { ConditionList as Conditions, TimeList as Times } from '@atlassian/eoc-rule-card';

import { LozengeWrapper, RuleCardContentContainer } from '../../../common/ui/styled';

import messages from './messages';
import { deletedEntityIndicatorStyle, transparentLozengeStyle } from './styled';
import { type RuleCardContentProps } from './types';

const DeletedEntityIndicator = () => {
	const { formatMessage } = useIntl();

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		<div style={deletedEntityIndicatorStyle}>
			<ErrorIcon label="Error icon" />
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
			<Text as="span" weight="semibold">
				{formatMessage(messages.deletedEntity)}
			</Text>
		</div>
	);
};

export const RuleCardContent = ({ entity, index }: RuleCardContentProps) => {
	const { formatMessage } = useIntl();

	return (
		<RuleCardContentContainer>
			<RuleItemGroup
				left={
					<LozengeWrapper>
						<Lozenge appearance="inprogress">
							{formatMessage(index === 0 ? messages.if : messages.elseIf)}
						</Lozenge>
					</LozengeWrapper>
				}
			>
				<RuleItemRow>
					<Flex flexDirection="column" rowGap={0.5}>
						{
							<Conditions
								data={entity.conditions}
								matchType={entity.conditionMatchType}
								noConditionMessage={formatMessage(messages.forAny)}
							/>
						}

						{
							<Times
								data={entity.timeRestrictedInterval.timeRestrictions}
								enabled={entity.timeRestrictedInterval.enabled}
								beforeMessage={formatMessage(messages.routingTimeIsBetween)}
							/>
						}
					</Flex>
				</RuleItemRow>
			</RuleItemGroup>
			<RuleItemGroup
				left={
					<LozengeWrapper>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
						<Lozenge style={transparentLozengeStyle}>{formatMessage(messages.then)}</Lozenge>
					</LozengeWrapper>
				}
			>
				<RuleItemRow>
					{formatMessage(messages.routeAlertTo, {
						entityName: entity.deleted ? (
							<DeletedEntityIndicator />
						) : !entity.entityId ? (
							formatMessage(messages.noOne)
						) : (
							entity.entityName
						),
						strong: (str) => <Text as="strong">{str}</Text>,
					})}
					{entity.entityType === 'sch' && (
						<Tooltip content={formatMessage(messages.scheduleWarning)}>
							<Flex alignItems="center">
								<EditorPanelIcon
									label={formatMessage(messages.scheduleWarning)}
									LEGACY_size="small"
								/>
							</Flex>
						</Tooltip>
					)}
				</RuleItemRow>
			</RuleItemGroup>
		</RuleCardContentContainer>
	);
};
